import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCsv } from '@phosphor-icons/react';
import PageWrapper from './PageWrapper';
import CsvUploader from './stream/CsvUploader';
import SaveStreamModal from './stream/SaveStreamModal';
import StreamMetricsCards from './stream/StreamMetricsCards';
import StreamTable from './stream/StreamTable';
import './stream/stream.css';


export default function StreamReview() {
  const navigate = useNavigate();
  const [streamData, setStreamData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSavingStream, setIsSavingStream] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedCsvContent, setUploadedCsvContent] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedRowCount, setUploadedRowCount] = useState(0);
  const [savedStreamId, setSavedStreamId] = useState(null);
  const [savedStreamMessage, setSavedStreamMessage] = useState('');
  const [alreadySaved, setAlreadySaved] = useState(false);
  const [alreadyExported, setAlreadyExported] = useState(false);
  const [streamers, setStreamers] = useState([]);
  const [breakCategories, setBreakCategories] = useState([]);
  const [breakSubcategories, setBreakSubcategories] = useState([]);
  const [selectedStreamerId, setSelectedStreamerId] = useState('');
  const [selectedBreakCategoryId, setSelectedBreakCategoryId] = useState('');
  const [selectedBreakSubcategoryId, setSelectedBreakSubcategoryId] = useState('');
  const [isLoadingLookups, setIsLoadingLookups] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const getUserSession = () => {
    try {
      const userDataRaw = localStorage.getItem('user');
      if (!userDataRaw) return null;
      const userData = JSON.parse(userDataRaw);
      if (!userData?.accessToken) return null;
      return userData;
    } catch (lookupError) {
      return null;
    }
  };

  const buildSummaryPayload = () => {
    if (!streamData) return null;

    return {
      totalRevenue: streamData.totalRevenue,
      totalCost: streamData.totalCost,
      totalFees: streamData.totalFees,
      totalStandardVAT: streamData.totalStandardVAT,
      totalGlobalVAT: streamData.totalGlobalVAT,
      totalVAT: streamData.totalVAT,
      grossProfit: streamData.grossProfit,
      netProfit: streamData.netProfit,
      profitMargin: streamData.profitMargin,
      revenueWithCost: streamData.revenueWithCost,
      feesWithCost: streamData.feesWithCost,
      vatWithCost: streamData.vatWithCost,
      stats: streamData.stats,
    };
  };

  const filteredBreakSubcategories = useMemo(() => {
    if (!selectedBreakCategoryId) return [];
    return breakSubcategories.filter(
      (subcategory) => String(subcategory.breakCategoryId) === String(selectedBreakCategoryId)
    );
  }, [breakSubcategories, selectedBreakCategoryId]);

  useEffect(() => {
    let isMounted = true;

    const loadLookups = async () => {
      const session = getUserSession();
      if (!session) return;

      setIsLoadingLookups(true);
      try {
        const response = await fetch('/api/stream/lookups', {
          headers: {
            Authorization: `Bearer ${session.accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load lookups (${response.status})`);
        }

        const payload = await response.json();
        if (!isMounted) return;

        const streamerItems = Array.isArray(payload.streamers) ? payload.streamers : [];
        const categoryItems = Array.isArray(payload.breakCategories) ? payload.breakCategories : [];
        const subcategoryItems = Array.isArray(payload.breakSubcategories) ? payload.breakSubcategories : [];

        setStreamers(streamerItems);
        setBreakCategories(categoryItems);
        setBreakSubcategories(subcategoryItems);

        const defaultStreamerId = selectedStreamerId || (streamerItems[0] ? String(streamerItems[0].id) : '');
        const defaultCategoryId = selectedBreakCategoryId || (categoryItems[0] ? String(categoryItems[0].id) : '');

        if (!selectedStreamerId && defaultStreamerId) {
          setSelectedStreamerId(defaultStreamerId);
        }
        if (!selectedBreakCategoryId && defaultCategoryId) {
          setSelectedBreakCategoryId(defaultCategoryId);
        }

        const categorySubcategories = subcategoryItems.filter(
          (subcategory) => String(subcategory.breakCategoryId) === String(defaultCategoryId)
        );
        if (!selectedBreakSubcategoryId && categorySubcategories.length > 0) {
          setSelectedBreakSubcategoryId(String(categorySubcategories[0].id));
        }
      } catch (lookupError) {
        console.error('Failed to load stream lookups:', lookupError);
        if (isMounted) {
          setSavedStreamMessage('Stream storage is not ready yet. Apply the phase-1 Supabase migration first.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingLookups(false);
        }
      }
    };

    loadLookups();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedBreakCategoryId) return;
    if (filteredBreakSubcategories.length === 0) {
      setSelectedBreakSubcategoryId('');
      return;
    }

    const hasSelected = filteredBreakSubcategories.some(
      (subcategory) => String(subcategory.id) === String(selectedBreakSubcategoryId)
    );

    if (!hasSelected) {
      setSelectedBreakSubcategoryId(String(filteredBreakSubcategories[0].id));
    }
  }, [filteredBreakSubcategories, selectedBreakCategoryId, selectedBreakSubcategoryId]);

  const handleProcessed = ({ processedData, csvContent, fileName, rowCount }) => {
    setStreamData(processedData);
    setUploadedCsvContent(csvContent);
    setUploadedFileName(fileName);
    setUploadedRowCount(rowCount);
    setSavedStreamId(null);
    setSavedStreamMessage('');
    setAlreadySaved(false);
    setAlreadyExported(false);
    setError(null);
  };

  const handleSaveStream = async () => {
    if (!streamData) {
      setError('Upload and process a stream CSV first.');
      return;
    }
    if (alreadySaved) {
      setError('This stream has already been saved. Upload a new CSV to save a different stream.');
      return;
    }

    if (!uploadedCsvContent || !uploadedFileName) {
      setError('Raw CSV content is missing. Please re-upload the stream CSV.');
      return;
    }

    if (!selectedStreamerId || !selectedBreakCategoryId || !selectedBreakSubcategoryId) {
      setError('Select a streamer, category, and subcategory before saving the stream.');
      return;
    }

    const session = getUserSession();
    if (!session) {
      setError('Your session has expired. Please log in again.');
      return;
    }

    setIsSavingStream(true);
    setError(null);
    setSavedStreamMessage('');

    try {
      const payload = {
        fileName: uploadedFileName,
        csvContent: uploadedCsvContent,
        csvRowCount: uploadedRowCount,
        streamerId: Number(selectedStreamerId),
        breakCategoryId: Number(selectedBreakCategoryId),
        breakSubcategoryId: Number(selectedBreakSubcategoryId),
        sourcePlatform: 'whatnot',
        streamDate: streamData?.allGlobalSchemeItems?.[0]?.timestamp || null,
        summary: buildSummaryPayload(),
      };

      const response = await fetch('/api/stream/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.details || result?.message || `Save failed (${response.status})`);
      }

      const streamId = result?.stream?.id || result?.id || null;
      setSavedStreamId(streamId);
      setAlreadySaved(true);
      if (result?.duplicate) {
        setAlreadyExported(!!(result?.stream?.published_to_sheets_at));
        setSavedStreamMessage('This stream was already saved.');
      } else {
        setSavedStreamMessage(result?.message || 'Stream saved successfully.');
      }
      setSaveModalOpen(false);
    } catch (saveError) {
      console.error('Save stream error:', saveError);
      setError(`Failed to save stream: ${saveError.message}`);
    } finally {
      setIsSavingStream(false);
    }
  };

  const handleExportGlobalScheme = async () => {
    if (!streamData || !streamData.globalSchemeItems || streamData.globalSchemeItems.length === 0) {
      setError('No Global Scheme items to export');
      return;
    }
    if (alreadyExported) {
      setError('Global margin scheme already exported for this stream. Contact an admin to re-export.');
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      let accessToken = '';
      let userName = 'Staff Portal User';

      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          if (user && user.accessToken) {
            accessToken = user.accessToken;
          } else {
            throw new Error('No access token found. Please log in again with an approved company account.');
          }

          if (user && user.name) {
            userName = user.name;
          }
        } else {
          throw new Error('You must be logged in with an approved company account to export to Google Sheets.');
        }
      } catch (localStorageError) {
        console.error('Error accessing localStorage:', localStorageError);
        if (
          localStorageError.message.includes('access token') ||
          localStorageError.message.includes('logged in')
        ) {
          throw localStorageError;
        }
        throw new Error('Failed to access login information. Please try logging in again.');
      }

      const exportData = {
        items: streamData.globalSchemeItems,
        summary: {
          totalRevenue: streamData.totalRevenue,
          totalCost: streamData.totalCost,
          totalFees: streamData.totalFees,
          totalGlobalVAT: streamData.totalGlobalVAT,
          netProfit: streamData.netProfit,
          exportDate: new Date().toISOString(),
        },
        userName,
        streamUploadId: savedStreamId || null,
      };

      const headers = {
        'Content-Type': 'application/json',
      };

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await fetch('/api/stream/export-global-scheme', {
        method: 'POST',
        headers,
        body: JSON.stringify(exportData),
      });

      let errorDetail = '';

      if (!response.ok) {
        try {
          const errorResponse = await response.json();
          errorDetail = errorResponse.message || '';
          if (errorResponse.details) {
            errorDetail += ` - ${errorResponse.details}`;
          }
        } catch (parseError) {
          errorDetail = await response.text();
        }

        if (response.status === 401) {
          localStorage.removeItem('user');
          setError('Google Sheets authentication failed. Redirecting to login page...');
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
          throw new Error(
            `Google Sheets authentication error: ${errorDetail || 'No valid authentication method available'}`
          );
        }

        throw new Error(`Export failed (${response.status}): ${errorDetail}`);
      }

      await response.json();
      setAlreadyExported(true);
      alert(`Successfully exported ${streamData.globalSchemeItems.length} Global Scheme items to Google Sheets`);
    } catch (exportError) {
      console.error('Export error:', exportError);

      if (exportError.message.includes('authentication') || exportError.message.includes('auth')) {
        setError(
          'Google Sheets authentication failed. Please make sure you are logged in with an approved company account that has access to the spreadsheet.'
        );
      } else {
        setError(`Failed to export Global Scheme items: ${exportError.message}`);
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageWrapper
      title="Stream Review"
      subtitle="Upload a Whatnot CSV to review stream metrics, detect cancelled rows, calculate VAT, and save the stream."
      action={
        <>
          {streamData?.globalSchemeItems?.length ? (
            <button
              className="rc-btn"
              onClick={handleExportGlobalScheme}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting…' : '↑ Export Global Scheme'}
            </button>
          ) : null}
          <button
            className="rc-btn rc-btn--primary"
            onClick={() => setSaveModalOpen(true)}
            disabled={!streamData || isLoadingLookups}
          >
            {savedStreamId ? '✓ Saved to Database' : '↑ Save Stream'}
          </button>
        </>
      }
    >
      {error && <div className="rc-alert rc-alert--error srv-message">⚠️ {error}</div>}
      {savedStreamMessage && <div className="rc-alert rc-alert--success srv-message">✅ {savedStreamMessage}</div>}

      <CsvUploader
        onProcessed={handleProcessed}
        onError={setError}
        onUploadStateChange={setIsUploading}
        uploadedFileName={uploadedFileName}
        uploadedRowCount={uploadedRowCount}
        hasStreamData={Boolean(streamData)}
      />

      {streamData ? (
        <>
          <StreamMetricsCards streamData={streamData} />
          <StreamTable streamData={streamData} />
        </>
      ) : (
        <div className="rc-empty srv-empty">
          <span className="rc-empty__icon"><FileCsv size={20} aria-hidden="true" /></span>
          <p className="rc-empty__body">
            Upload a Whatnot CSV above to review stream metrics, detect cancelled rows, calculate VAT, and save the stream.
          </p>
        </div>
      )}

      <SaveStreamModal
        opened={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        streamers={streamers}
        breakCategories={breakCategories}
        filteredBreakSubcategories={filteredBreakSubcategories}
        selectedStreamerId={selectedStreamerId}
        selectedBreakCategoryId={selectedBreakCategoryId}
        selectedBreakSubcategoryId={selectedBreakSubcategoryId}
        setSelectedStreamerId={setSelectedStreamerId}
        setSelectedBreakCategoryId={setSelectedBreakCategoryId}
        setSelectedBreakSubcategoryId={setSelectedBreakSubcategoryId}
        isLoadingLookups={isLoadingLookups}
        isSavingStream={isSavingStream}
        uploadedFileName={uploadedFileName}
        uploadedRowCount={uploadedRowCount}
        savedStreamMessage={savedStreamMessage}
        onSave={handleSaveStream}
      />

      {isUploading && (
        <p className="srv-progress">
          Upload in progress…
        </p>
      )}
    </PageWrapper>
  );
}
